/**
 * CheckoutSuccess.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { getIcon } from '../utils/icons';
import { inferCheckoutPaymentMode } from '../utils/paymentSandbox';
import { useLanguage } from '../context/LanguageContext';
import './Checkout.css';

const CheckoutSuccess = () => {
  const { t } = useLanguage();
  const s = t.shop.success;
  const cartUi = t.shop.cart;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [orderDetails, setOrderDetails] = useState(null);
  const [hadDigitalDownloads, setHadDigitalDownloads] = useState(false);
  const [hadPhysicalItems, setHadPhysicalItems] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null); // 'sandbox' | 'live' | null
  const hasProcessed = useRef(false); // Track if we've already processed this session

  // Extract primitive values for stable dependencies
  const sessionId = searchParams.get('session_id');
  const provider = searchParams.get('provider');

  // Prefer session id evidence immediately (cs_live_ / cs_test_) before API returns
  useEffect(() => {
    const inferred = inferCheckoutPaymentMode({ sessionId, provider });
    if (inferred) setPaymentMode(inferred);
  }, [sessionId, provider]);

  useEffect(() => {
    // Prevent duplicate processing
    if (hasProcessed.current) {
      console.log('⏭️  CheckoutSuccess: Already processed, skipping');
      return;
    }

    // Mark as processed immediately to prevent duplicates from Strict Mode
    hasProcessed.current = true;

    const completeOrder = async () => {
      const startTime = Date.now();
      console.log('🚀 [START] CheckoutSuccess at:', new Date().toISOString());
      console.log('✅ CheckoutSuccess: Loaded with params:', { sessionId, provider });

      if (!sessionId || provider !== 'stripe') {
        console.error('❌ CheckoutSuccess: Invalid params');
        setError(s.invalidSession);
        setIsProcessing(false);
        return;
      }

      try {
        console.log('💳 [STEP 1] Calling /api/payment/complete-stripe-order');

        const response = await fetch('/api/payment/complete-stripe-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId })
        });

        console.log('💳 CheckoutSuccess: Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ CheckoutSuccess: Response not OK:', errorText);
          throw new Error(`Server error: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('💳 CheckoutSuccess: Result:', result);

        if (result.success) {
          const mode = inferCheckoutPaymentMode({
            sessionId,
            provider: 'stripe',
            stripeMode: result.stripeMode,
            paymentMode: result.paymentMode,
          });
          if (mode) setPaymentMode(mode);

          const digitalFlag = sessionStorage.getItem('checkoutHadDigital') === '1';
          const physicalFlag = sessionStorage.getItem('checkoutHadPhysical') === '1';
          if (digitalFlag) sessionStorage.removeItem('checkoutHadDigital');
          if (physicalFlag) sessionStorage.removeItem('checkoutHadPhysical');
          // Fallback: if neither flag was set (older sessions), assume physical shipping flow
          const hasDigital = digitalFlag;
          const hasPhysical = physicalFlag || !digitalFlag;
          setHadDigitalDownloads(hasDigital);
          setHadPhysicalItems(hasPhysical);

          setOrderDetails({
            orderNumber: result.orderNumber,
            emailsSent: result.emailsSent,
            paymentMode: mode,
          });

          clearCart();
        } else {
          throw new Error(result.error || s.failedComplete);
        }
      } catch (err) {
        console.error('❌ [ERROR] CheckoutSuccess: Error completing order:', err);
        setError(err.message || s.failedComplete);
      } finally {
        console.log('🏁 [FINALLY] Setting isProcessing to false');
        console.log('⏱️  Total execution time:', Date.now() - startTime, 'ms');
        setIsProcessing(false);
      }
    };

    completeOrder();
    // Intentionally keyed only on session params; effect body logs one-shot and uses latest state setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, provider, clearCart]);

  if (isProcessing) {
    return (
      <div className="checkout-container">
        <div className="checkout-content">
          <div className="processing-overlay">
            <div className="spinner"></div>
            <p>{s.completing}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-container">
        <div className="checkout-content">
          <div className="order-confirmation-card error">
            <div className="confirmation-icon error">❌</div>
            <h2>{s.errorTitle}</h2>
            <p className="error-message">{error}</p>
            <button
              className="continue-shopping-btn"
              onClick={() => navigate('/checkout')}
            >
              {getIcon('back')} {s.backToCheckout}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return null;
  }

  const isSandboxOrder = paymentMode === 'sandbox';
  const isLiveOrder = paymentMode === 'live';
  const isMixedOrder = hadDigitalDownloads && hadPhysicalItems;

  return (
    <div className="checkout-container success-page">
      <div className="checkout-content">

        {isSandboxOrder && (
        <div className="test-notice-banner test-notice-banner--sandbox">
          <p style={{ margin: '0' }}>
            <strong>🧪 {s.sandboxBanner}</strong>
          </p>
        </div>
        )}

        {isLiveOrder && (
        <div
          className="test-notice-banner"
          style={{
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '1px solid #86efac',
            color: '#14532d',
          }}
        >
          <p style={{ margin: '0' }}>
            <strong>💳 {s.liveBanner}</strong>
          </p>
        </div>
        )}

        <div className="order-confirmation-card success">
          <div className="confirmation-header">
            <h1>🎉 {s.confirmed}</h1>
            <p className="confirmation-subtitle">{s.thankYou}</p>
          </div>

          <div className="order-details-box">
            <div className="order-number-badge">
              <span className="badge-label">{s.orderNumber}</span>
              <span className="badge-number">{orderDetails.orderNumber}</span>
            </div>
          </div>

          <div className="success-message-box">
            <div className="message-icon">📧</div>
            <div className="message-content">
              <h3>{s.emailSent}</h3>
              <p>{s.emailSentText}</p>
            </div>
          </div>

          <div className="what-happens-next">
            <h3>
              {hadDigitalDownloads ? '📥' : '📦'} {s.next}
            </h3>
            <div className="timeline">
              <div className="timeline-item completed">
                <div className="timeline-icon">✓</div>
                <div className="timeline-content">
                  <h4>{s.received}</h4>
                  <p>{s.receivedText}</p>
                </div>
              </div>
              {hadDigitalDownloads && (
                <>
                  <div className="timeline-item">
                    <div className="timeline-icon">⏳</div>
                    <div className="timeline-content">
                      <h4>{s.review}</h4>
                      <p>{s.reviewText}</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-icon">📥</div>
                    <div className="timeline-content">
                      <h4>{s.download}</h4>
                      <p>{s.downloadText}</p>
                    </div>
                  </div>
                  {!isMixedOrder && (
                    <div className="timeline-item">
                      <div className="timeline-icon">🔁</div>
                      <div className="timeline-content">
                        <h4>{s.redownload}</h4>
                        <p>{s.redownloadText}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
              {hadPhysicalItems && (
                <>
                  <div className="timeline-item">
                    <div className="timeline-icon">📋</div>
                    <div className="timeline-content">
                      <h4>{s.processing}</h4>
                      <p>{s.processingText}</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-icon">📦</div>
                    <div className="timeline-content">
                      <h4>{s.shipping}</h4>
                      <p>{s.shippingText}</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-icon">🏠</div>
                    <div className="timeline-content">
                      <h4>{s.delivery}</h4>
                      <p>{s.deliveryText}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="primary-action-btn"
              onClick={() => { window.location.href = '/shop'; }}
            >
              {getIcon('shop')} {cartUi.continueShopping}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
